import { prefix, ajax } from './common';
import { getToken } from '@developers/single-sign-on';
import { config } from '../utils/config';

export async function getValidatorToken(key = '', secure_id = '') {
  return await ajax({
    method: 'POST',
    url: prefix + '/orders/actions',
    data: {
      type: 'validateForOrderStatus',
      key: key,
      secure_id: secure_id
    }
  });
}

export async function getValidatorTokenFromForm(order_id, purchase_key) {
  return await ajax({
    method: 'POST',
    url: prefix + '/orders/actions',
    data: {
      type: 'validateForOrderStatus',
      order_id: order_id,
      purchase_key: purchase_key,
    }
  });
}

export async function modifyOrder(key, formData) {
  return await ajax({
    method: 'POST',
    url: prefix + '/orders/actions',
    data: {
      type: 'modifyOrCancel',
      key: key,
      action: formData.action,
      reason: formData.reason,
      extra_info: formData.extra_info
    }
  });
}

export async function getOrderDetails(order_id, token = '', attributes = 'is_warranty') {
  return await ajax({
    url: prefix + '/orders/' + order_id,
    data: {
      key: token,
      attributes: attributes
    }
  });
}

export async function getCreditCardUsedOnOrder(order_id, token = '') {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/credit-card',
    data: {
      key: token
    }
  });
}

export async function updateCreditCard(order_id, creditCardInfo, token = '') {
  return await ajax({
    method: 'PUT',
    url: prefix + '/orders/' + order_id + '/credit-card',
    data: {
      key: token,
      purchase_card_name: creditCardInfo.name,
      purchase_card_company: creditCardInfo.company,
      purchase_card_address_1: creditCardInfo.address1,
      purchase_card_address_2: creditCardInfo.address2,
      purchase_card_city: creditCardInfo.city,
      purchase_card_state_id: creditCardInfo.state,
      purchase_card_zip: creditCardInfo.postalCode,
      purchase_card_address_block: creditCardInfo.addressBlock,
      cc_number: creditCardInfo.cardNumber,
      purchase_card_exp_month: creditCardInfo.expMonth,
      purchase_card_exp_year: creditCardInfo.expYear,
      purchase_card_cvv2: creditCardInfo.cvv,
      purchase_payment_type: 'credit card'
    }
  });
}

export async function getTrackingInfo(order_id, token = '') {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/tracking-numbers',
    data: {
      key: token
    }
  });
}

export async function getDamageClaimInfo(order_id, token = '') {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/damage-claims',
    data: {
      key: token
    }
  });
}

export async function getDamageClaimProducts(dc_id, token = '') {
  return await ajax({
    url: prefix + '/damage-claims/' + dc_id + '/products',
    data: {
      key: token
    }
  });
}

export async function getProducts(order_id, token = '') {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/products',
    data: {
      key: token
    }
  });
}

export async function getCoreReturnDetails(order_id, token = '') {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/core-returns',
    data: {
      key: token
    }
  });
}

export async function getReturnAuthorizationDetails(order_id, token = '') {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/return-authorizations',
    data: {
      key: token
    }
  });
}

export async function getReturnAuthorizationProducts(ra_id, token = '') {
  return await ajax({
    url: prefix + '/return-authorizations/' + ra_id + '/products',
    data: {
      key: token
    }
  });
}

export async function createReturnAuthorization(form, token = '') {
  const data = new FormData();
  
  // Form
  data.append('purchase_id', form.purchase_id);
  data.append('key', token);
  data.append('reason', form.reason);
  data.append('additional_details', form.additional_details);
  data.append('brand_id', form.brand.id);
  data.append('brand_name', form.brand.name);
  data.append('equipment_id', form.model.id);
  data.append('equipment_name', form.model.name);
  data.append('equipment_serial', form.serial_number);
  data.append('ra_refund', form.refund_total);
  data.append('ra_min_order_charge', form.min_order_charge);
  data.append('products', JSON.stringify(form.items));
  data.append('electrical_part_questions', JSON.stringify(form.electrical_part_questions));
  data.append('contact_method', form.contact_method);
  data.append('contact_time', form.contact_time);
  
  // Pictures
  data.append('model_num_picture', form.model_num_picture);
  data.append('serial_num_picture', form.serial_num_picture);

  /**
   *  File name convention for pictures below is type of picture and catalog number seperated by dashes
   *  (example: defective-part-62000012)
   * 
   *  This convention is required by the backend. If it changes here, the backend will also need to be changed 
   *  */ 
  form.defective_part_pictures.forEach((picture) => {
    data.append(picture.name, picture);
  });

  form.defective_part_barcode_pictures.forEach((picture) => {
    data.append(picture.name, picture);
  });

  form.part_comparison_pictures.forEach((picture) => {
    data.append(picture.name, picture);
  });

  form.measurements_pictures.forEach((picture) => {
    data.append(picture.name, picture);
  });

  form.part_not_working_pictures.forEach((picture) => {
    data.append(picture.name, picture);
  });

  form.returning_part_barcode_pictures.forEach((picture) => {
    data.append(picture.name, picture);
  });
  
  data.append('screenshot', form.screenshot);
  
  return await fetch(prefix + '/return-authorizations', {
    method: 'POST',
    headers: new Headers({
      sso: getToken(config('sso_storage_driver')),
    }),
    body: data,
  });
}

export async function getOrderDetailsForShipping(order_id, depot_id, carrier) {
  return await ajax({
    url: prefix + '/orders/' + order_id + '/shipping/order-details',
    data: {
      depot_id: depot_id,
      carrier: carrier
    }
  });
}

export async function shipOrder(form) {
  return await ajax({
    method: 'POST',
    url: prefix + '/orders/' + form.order_number + '/shipping/label/create',
    data: {
      order_number: form.order_number,
      depot_id: form.depot,
      carrier: form.carrier,
      to_address: {
        name: form.to_address.name,
        company: form.to_address.company,
        phone: form.to_address.phone,
        address_line_1: form.to_address.address_line_1,
        address_line_2: form.to_address.address_line_2,
        city: form.to_address.city,
        state: form.to_address.state,
        postal_code: form.to_address.postal_code,
        country: 'US'
      },
      package: {
        weight: form.package.weight,
        length: form.package.length,
        width: form.package.width,
        height: form.package.height
      }
    }
  });
}

export async function getRecentLabels(limit = 5) {
  return await ajax({
    url: prefix + '/orders/shipping/labels',
    data: {
        limit
    }
  });
}

export async function voidOrder(order_number, tracking_number) {
  return await ajax({
    method: 'DELETE',
    url: prefix + '/orders/' + order_number + '/shipping/label/void',
    data: {
      tracking_number
    }
  });
}